import React, { useEffect, useMemo, useRef, useState } from 'react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { COMPANY_API, PROFILE_COMPANY_PIC, getApiUrl } from '../../helpers/url_helper';
import { useThemeMode } from '../../ThemeContext';

const FolderOutlineIcon = ({ color = 'currentColor', size = 26 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  </svg>
);

function formatItemDate(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const KebabIcon = ({ color = 'currentColor', size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="5" r="1.4" />
    <circle cx="12" cy="12" r="1.4" />
    <circle cx="12" cy="19" r="1.4" />
  </svg>
);

export default function AssistantProjectStartView({
  mode: modeProp = 'light',
  projectId = null,
  projectTitle = 'Project',
  items = [],
  onSubmit,
  onSessionClick
}) {
  const { mode: contextMode } = useThemeMode();
  const mode = contextMode || modeProp;
  const [inputValue, setInputValue] = useState('');
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [openMenuSessionKey, setOpenMenuSessionKey] = useState(null);
  const [hoveredSessionKey, setHoveredSessionKey] = useState(null);
  const [editingSessionKey, setEditingSessionKey] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadedDocumentUrl, setUploadedDocumentUrl] = useState(null);
  const [documentContext, setDocumentContext] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');

  useEffect(() => {
    const pid = projectId != null ? String(projectId) : '';
    if (!pid) {
      setSessions([]);
      setIsLoading(false);
      setLoadError('');
      return;
    }

    const controller = new AbortController();

    const fetchSessions = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const token = localStorage.getItem('authToken');
        const endpoint = `company/conversation-projects/${encodeURIComponent(pid)}/chat-sessions`;

        const res = await fetch(getApiUrl(endpoint), {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          signal: controller.signal
        });

        const json = await res.json().catch(() => null);
        if (!res.ok) {
          const msg = json?.message || `Request failed (${res.status})`;
          throw new Error(msg);
        }

        const chatSessions = json?.data?.chat_sessions;
        setSessions(Array.isArray(chatSessions) ? chatSessions : []);
      } catch (e) {
        if (e?.name === 'AbortError') return;
        setLoadError(e?.message || 'Failed to load sessions');
        setSessions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessions();

    return () => controller.abort();
  }, [projectId]);

  const palette = useMemo(() => {
    const isDark = mode === 'dark';
    return {
      pageBg: 'transparent',
      text: isDark ? '#ffffff' : '#1a1a1a',
      muted: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
      border: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
      rowBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
      inputBg: isDark ? '#141414' : '#ffffff'
    };
  }, [mode]);

  const handleSubmit = () => {
    const text = inputValue.trim();
    const hasAttachment = !!uploadedImageUrl || !!selectedDocument || !!(documentContext && documentContext.trim().length > 0);
    if (!text && !hasAttachment) return;
    onSubmit?.(text, {
      imagePreviewUrl,
      uploadedImageUrl,
      selectedDocument,
      uploadedDocumentUrl,
      documentContext
    });
    setInputValue('');
    setSelectedImage(null);
    setImagePreviewUrl(null);
    setUploadedImageUrl(null);
    setSelectedDocument(null);
    setUploadedDocumentUrl(null);
    setDocumentContext(null);
    setAttachmentError('');
  };

  const getFullFileUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return PROFILE_COMPANY_PIC + path.replace(/^\//, '');
  };

  const handleAttachClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddImage = () => {
    handleMenuClose();
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.accept = 'image/*';
      fileInputRef.current.click();
    }
  };

  const handleAddFile = () => {
    handleMenuClose();
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.accept = 'application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt';
      fileInputRef.current.click();
    }
  };

  const uploadChatImage = async (file) => {
    const token = localStorage.getItem('authToken');
    const formData = new FormData();
    formData.append('file', file);
    setAttachmentError('');
    try {
      const response = await fetch(getApiUrl(COMPANY_API.CHAT_IMAGE_UPLOAD), {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: formData
      });
      const data = await response.json().catch(() => null);
      if (data?.status === 'success' && data?.data?.url) {
        const url = getFullFileUrl(data.data.url);
        setUploadedImageUrl(url);
        setImagePreviewUrl(url);
        setSelectedDocument(null);
        setUploadedDocumentUrl(null);
        setDocumentContext(null);
      } else {
        setSelectedImage(null);
        setImagePreviewUrl(null);
        setUploadedImageUrl(null);
        setAttachmentError('Failed to upload image. Please try again.');
      }
    } catch (e) {
      setSelectedImage(null);
      setImagePreviewUrl(null);
      setUploadedImageUrl(null);
      setAttachmentError('Failed to upload image. Please try again.');
    }
  };

  const uploadChatDocument = async (file) => {
    const token = localStorage.getItem('authToken');
    const formData = new FormData();
    formData.append('file', file);
    setAttachmentError('');
    try {
      const response = await fetch(getApiUrl(COMPANY_API.CHAT_IMAGE_UPLOAD), {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: formData
      });
      const data = await response.json().catch(() => null);
      if (data?.status === 'success' && data?.data?.url) {
        const url = getFullFileUrl(data.data.url);
        setUploadedDocumentUrl(url);
        setSelectedDocument(file);
        setDocumentContext(data?.data?.doc_context || null);
        setSelectedImage(null);
        setImagePreviewUrl(null);
        setUploadedImageUrl(null);
      } else {
        setSelectedDocument(null);
        setUploadedDocumentUrl(null);
        setDocumentContext(null);
        setAttachmentError('Failed to upload document. Please try again.');
      }
    } catch (e) {
      setSelectedDocument(null);
      setUploadedDocumentUrl(null);
      setDocumentContext(null);
      setAttachmentError('Failed to upload document. Please try again.');
    }
  };

  const deleteUploadedAsset = async (url, type) => {
    const token = localStorage.getItem('authToken');
    try {
      let fullUrlToSend = url || '';
      if (fullUrlToSend && !/^https?:\/\//i.test(fullUrlToSend)) {
        fullUrlToSend = PROFILE_COMPANY_PIC + fullUrlToSend.replace(/^\//, '');
      }
      const response = await fetch(getApiUrl(COMPANY_API.DELETE_CHAT_IMAGE), {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
          accept: 'application/json'
        },
        body: JSON.stringify({ url: fullUrlToSend })
      });
      const data = await response.json().catch(() => null);
      if (data?.status !== 'success') {
        setAttachmentError(`Failed to delete ${type}. Please try again.`);
      }
    } catch (e) {
      setAttachmentError(`Failed to delete ${type}. Please try again.`);
    }
  };

  const clearImage = async () => {
    if (uploadedImageUrl) {
      await deleteUploadedAsset(uploadedImageUrl, 'image');
    }
    setSelectedImage(null);
    setImagePreviewUrl(null);
    setUploadedImageUrl(null);
    setAttachmentError('');
  };

  const clearDocument = async () => {
    if (uploadedDocumentUrl) {
      await deleteUploadedAsset(uploadedDocumentUrl, 'document');
    }
    setSelectedDocument(null);
    setUploadedDocumentUrl(null);
    setDocumentContext(null);
    setAttachmentError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type.startsWith('image/')) {
      setSelectedImage(file);
      uploadChatImage(file);
      return;
    }

    const isDoc =
      file.type === 'application/pdf' ||
      file.type === 'application/msword' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-powerpoint' ||
      file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      file.type === 'text/plain';

    if (isDoc) {
      setSelectedDocument(file);
      uploadChatDocument(file);
      return;
    }

    setAttachmentError('Please select an image, PDF, Word, Excel, PowerPoint, or text file.');
  };

  const getSessionKey = (item) => {
    const key = item?.chat_session_id ?? item?.sessionId ?? item?.id;
    return key != null ? String(key) : '';
  };

  const getSessionTitle = (item) => item?.title || 'Conversation';

  const saveEditedTitle = async (item) => {
    const sessionId = item?.chat_session_id ?? item?.sessionId ?? item?.id;
    const key = getSessionKey(item);
    const newTitle = (editingTitle || '').trim();
    const oldTitle = getSessionTitle(item).trim();

    if (!key || sessionId == null || isSavingTitle) return;
    if (!newTitle) {
      setEditingTitle(oldTitle);
      setEditingSessionKey(null);
      return;
    }
    if (newTitle === oldTitle) {
      setEditingSessionKey(null);
      return;
    }

    setIsSavingTitle(true);
    setHistoryError('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(getApiUrl(`company/chat-sessions/${sessionId}/title`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ title: newTitle })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status !== 'success') {
        throw new Error(data?.message || 'Failed to update chat title. Please try again.');
      }

      const resolvedTitle = data?.data?.title || newTitle;
      setSessions((prev) =>
        prev.map((s) =>
          String(getSessionKey(s)) === key ? { ...s, title: resolvedTitle } : s
        )
      );
    } catch (e) {
      setHistoryError(e?.message || 'Failed to update chat title. Please try again.');
    } finally {
      setIsSavingTitle(false);
      setEditingSessionKey(null);
    }
  };

  const deleteSession = async (item) => {
    const sessionId = item?.chat_session_id ?? item?.sessionId ?? item?.id;
    const key = getSessionKey(item);
    if (!key || sessionId == null) return;

    setOpenMenuSessionKey(null);
    setHistoryError('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(getApiUrl(`company/chat-sessions/${sessionId}`), {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status !== 'success') {
        throw new Error(data?.message || 'Failed to delete chat. Please try again.');
      }

      setSessions((prev) => prev.filter((s) => String(getSessionKey(s)) !== key));
    } catch (e) {
      setHistoryError(e?.message || 'Failed to delete chat. Please try again.');
    }
  };

  return (
    <div
      className="assistant-main-column"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        background: palette.pageBg,
        color: palette.text
      }}
    >
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 24px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <FolderOutlineIcon color={palette.text} size={24} />
          <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.15 }}>{projectTitle}</div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            background: mode === 'dark'
              ? 'linear-gradient(135deg, #18191B 0%, #232323 100%)'
              : 'linear-gradient(135deg, #f8f9fa 0%, #e0f7f7 100%)',
            border: `1px solid ${mode === 'dark' ? '#232323' : '#ddd'}`,
            borderRadius: 14,
            minHeight: 56,
            boxShadow: mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.12)' : '0 1px 4px rgba(38,195,192,0.04)',
            padding: '0 0 0 0',
          }}
        >
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 12px',
              cursor: 'pointer',
              height: '100%',
            }}
            tabIndex={-1}
            aria-label="Attach file"
            onClick={handleAttachClick}
          >
            <AttachFileIcon style={{ color: '#b0b0b0', fontSize: 22 }} />
          </button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <MenuItem onClick={handleAddImage}>
              <ImageOutlinedIcon style={{ marginRight: 8, color: mode === 'dark' ? '#26c3c0' : '#009391' }} />
              Image
            </MenuItem>
            <MenuItem onClick={handleAddFile}>
              <InsertDriveFileOutlinedIcon style={{ marginRight: 8, color: mode === 'dark' ? '#26c3c0' : '#009391' }} />
              File
            </MenuItem>
          </Menu>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {imagePreviewUrl && (
            <div style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              margin: '12px',
              borderRadius: 14,
              boxShadow: mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.24)' : '0 2px 8px rgba(38,195,192,0.08)',
              background: mode === 'dark' ? '#18191B' : '#fff',
              padding: 0,
              width: 180,
              minHeight: 70
            }}>
              <div style={{ position: 'relative', width: '100%' }}>
                <img
                  src={imagePreviewUrl}
                  alt="preview"
                  style={{
                    width: '100%',
                    maxHeight: 80,
                    objectFit: 'cover',
                    borderRadius: 14,
                    display: 'block',
                    background: '#f4f4f4'
                  }}
                />
                <button
                  onClick={clearImage}
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: '#18191Bcc',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    cursor: 'pointer',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                    fontSize: 14
                  }}
                  title="Remove image"
                  tabIndex={-1}
                >
                  ×
                </button>
              </div>
            </div>
          )}
          {selectedDocument && (
            <div style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              margin: '12px',
              borderRadius: 14,
              boxShadow: mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.24)' : '0 2px 8px rgba(38,195,192,0.08)',
              background: mode === 'dark' ? '#18191B' : '#fff',
              padding: '10px',
              width: 200,
              minHeight: 54
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                <InsertDriveFileOutlinedIcon style={{ fontSize: 18, color: '#26c3c0', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: mode === 'dark' ? '#fff' : '#333',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {selectedDocument.name}
                  </div>
                  <div style={{
                    fontSize: 10,
                    color: mode === 'dark' ? '#888' : '#666',
                    marginTop: 2
                  }}>
                    {(selectedDocument.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
                <button
                  onClick={clearDocument}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: '#18191Bcc',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: 12,
                    flexShrink: 0
                  }}
                  title="Remove document"
                  tabIndex={-1}
                >
                  ×
                </button>
              </div>
            </div>
          )}
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={(imagePreviewUrl || selectedDocument) ? 'Add a message about the file or image...' : `New chat in ${projectTitle}`}
            style={{
              flex: 1,
              height: 56,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: palette.text,
              fontSize: 16,
              fontWeight: 500,
              fontFamily: 'inherit',
              padding: '18px 0',
            }}
          />
        </div>
        {!!attachmentError && (
          <div style={{ color: '#f44336', fontSize: 12, marginTop: 8 }}>
            {attachmentError}
          </div>
        )}

        <div
          style={{
            marginTop: 18,
            maxWidth: 640,
            marginLeft: 'auto',
            marginRight: 'auto',
            overflow: 'visible'
          }}
        >
          {!!historyError && (
            <div style={{ color: '#f44336', fontSize: 12, padding: '4px 0 8px' }}>
              {historyError}
            </div>
          )}
          {isLoading && (
            <div style={{ color: palette.muted, fontSize: 13, padding: '8px 0' }}>
              Loading conversations...
            </div>
          )}
          {!!loadError && !isLoading && (
            <div style={{ color: palette.muted, fontSize: 13, padding: '8px 0' }}>
              {loadError}
            </div>
          )}

          {sessions
            .slice(0, 6)
            .map((item, idx) => (
            <div
              key={String(item?.chat_session_id ?? item?.id ?? item?.sessionId ?? idx)}
              onClick={() =>
                onSessionClick?.({
                  id: item?.chat_session_id ?? item?.id ?? item?.sessionId,
                  sessionId: item?.chat_session_id ?? item?.sessionId ?? item?.id,
                  title: item?.title || 'Conversation',
                  createdAt: item?.createdAt || item?.created_at
                })
              }
              style={{
                borderBottom: `1px solid ${palette.rowBorder}`,
                padding: '14px 0 12px',
                cursor: 'pointer',
                overflow: 'visible'
              }}
              onMouseEnter={() => setHoveredSessionKey(getSessionKey(item))}
              onMouseLeave={() =>
                setHoveredSessionKey((prev) =>
                  prev === getSessionKey(item) ? null : prev
                )
              }
            >
              <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  {editingSessionKey === getSessionKey(item) ? (
                    <input
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={() => saveEditedTitle(item)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          saveEditedTitle(item);
                        }
                        if (e.key === 'Escape') {
                          setEditingSessionKey(null);
                          setEditingTitle(getSessionTitle(item));
                        }
                      }}
                      style={{
                        width: '100%',
                        height: 28,
                        borderRadius: 6,
                        border: `1px solid ${mode === 'dark' ? '#444' : '#ccc'}`,
                        background: mode === 'dark' ? '#111' : '#fff',
                        color: palette.text,
                        padding: '0 8px',
                        fontSize: 13,
                        fontFamily: 'inherit'
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        fontSize: 16,
                        lineHeight: 1.15,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {getSessionTitle(item)}
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: 4,
                      color: palette.muted,
                      fontSize: 13,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {formatItemDate(item?.createdAt || item?.created_at)}
                  </div>
                </div>

                {editingSessionKey !== getSessionKey(item) && (
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const key = getSessionKey(item);
                        setOpenMenuSessionKey((prev) => (prev === key ? null : key));
                      }}
                      style={{
                        width: 24,
                        height: 24,
                        border: 'none',
                        borderRadius: 6,
                        display:
                          hoveredSessionKey === getSessionKey(item) ||
                          openMenuSessionKey === getSessionKey(item)
                            ? 'flex'
                            : 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                        color: palette.text,
                        cursor: 'pointer'
                      }}
                    >
                      <KebabIcon color={palette.text} size={14} />
                    </button>

                    {openMenuSessionKey === getSessionKey(item) && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          bottom: 26,
                          right: 0,
                          minWidth: 120,
                          borderRadius: 8,
                          border: mode === 'dark' ? '1px solid #333' : '1px solid #e6e6e6',
                          background: mode === 'dark' ? '#1a1a1a' : '#fff',
                          boxShadow: '0 10px 24px rgba(0,0,0,0.18)',
                          zIndex: 9999,
                          padding: 6
                        }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuSessionKey(null);
                            setEditingSessionKey(getSessionKey(item));
                            setEditingTitle(getSessionTitle(item));
                          }}
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'transparent',
                            textAlign: 'left',
                            padding: '8px 10px',
                            borderRadius: 6,
                            color: palette.text,
                            cursor: 'pointer',
                            fontSize: 13
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSession(item);
                          }}
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'transparent',
                            textAlign: 'left',
                            padding: '8px 10px',
                            borderRadius: 6,
                            color: '#f44336',
                            cursor: 'pointer',
                            fontSize: 13
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

